/**
 * permissions.js — declarative permission map for the /admin surface.
 *
 * Single source of truth consumed by:
 *   - <RequirePermission action="..."> route guard (frontend/src/components/RequirePermission.jsx)
 *   - <AdminSidebar/> filter (frontend/src/components/admin/AdminSidebar.jsx)
 *
 * Per the orchestrator plan, PR-B v1 only consults the `role` axis. The
 * `module` and `scope` axes are stubs that always evaluate to true:
 *   - PR-A.6 will wire `scope` (operator vs master vs viewer cross-tenant).
 *   - PR-C will wire `module` (per-tenant module activation).
 *
 * ADR reference: docs/adr/0001-role-hierarchy-and-tenancy.md (§5–§7).
 */
import { useAuth } from "../context/AuthContext.jsx";
import { ROLES, ALL_ROLES } from "./roles.js";

const { MASTER, OWNER } = ROLES;

/**
 * Permission map. Each entry declares the set of roles that may perform the
 * action. Unknown actions return `false` from `useRolePermission` (fail-closed).
 *
 * Naming: `verb_noun` — verbs are `view`, `manage`, `assign`, `edit`.
 */
export const PERMISSIONS = Object.freeze({
  // Account / profile
  view_account: { roles: ALL_ROLES },
  edit_own_account: { roles: ALL_ROLES },

  // Operators (master only — TenantsPage successor)
  manage_operators: { roles: [MASTER] },

  // Viewers (CRUD = master in PR-B baseline; operator gets read-only via view_viewers)
  manage_viewers: { roles: [MASTER] },
  view_viewers: { roles: [MASTER, OWNER] },
  assign_viewer_devices: { roles: [MASTER, OWNER] },

  // Devices
  view_devices: { roles: [MASTER, OWNER] },
  manage_devices: { roles: [MASTER] },

  // Drivers + vehicles
  manage_drivers: { roles: [MASTER, OWNER] },
  manage_vehicles: { roles: [MASTER, OWNER] },

  // Control (comandos, geocercas, configuración de alertas). Mismo alcance que
  // tenía el menú "Control" del TopNav antes de mudarse al rail de /admin.
  send_commands: { roles: [MASTER, OWNER] },
  manage_geofences: { roles: [MASTER, OWNER] },
  manage_alerts: { roles: [MASTER, OWNER] },

  // Billing (cobros placeholders — real implementation in PR-C)
  manage_billing: { roles: [MASTER, OWNER] },

  // Modules layer (master only — see ADR §7)
  manage_modules: { roles: [MASTER] },

  // KYC workflow surface
  view_kyc: { roles: [MASTER, OWNER] },

  // Inspections (revisiones / visitas)
  view_inspections: { roles: [MASTER, OWNER] },

  // Finanzas de Flota (contabilidad de vehículos en renta)
  view_finanzas: { roles: [MASTER, OWNER] },
  manage_finanzas: { roles: [MASTER, OWNER] },

  // Catalog (plans + features) — master only
  "catalog.read": { roles: [MASTER] },
  "catalog.create": { roles: [MASTER] },
  "catalog.update": { roles: [MASTER] },
  "catalog.delete": { roles: [MASTER] },
});

/**
 * useRolePermission — hook returning whether the current user is allowed to
 * perform `action` based purely on role (legacy snake_case PERMISSIONS map).
 *
 * NOT the same as the dot-notation `useCan(action)` from `hooks/useCan.js`,
 * which also gates on the tenant plan's features. This hook predates PR-5 and
 * is kept for `<RequirePermission/>`'s admin-route guard until that surface is
 * migrated to the dot-notation action vocabulary.
 *
 * Fails closed: returns `false` when the user is unauthenticated, the action
 * is unknown, or the user's role is not on the allow-list.
 *
 * @param {string} action — key from PERMISSIONS
 * @returns {boolean}
 */
export function useRolePermission(action) {
  const { user } = useAuth();
  return canRole(user?.role, action);
}

/**
 * canRole — pure helper for unit tests + non-hook contexts (e.g. Sidebar
 * filtering inside a render). Equivalent to `useRolePermission` minus the hook plumbing.
 *
 * @param {string|null|undefined} role
 * @param {string} action
 * @returns {boolean}
 */
export function canRole(role, action) {
  const perm = PERMISSIONS[action];
  if (!perm || !role) return false;
  return perm.roles.includes(role);
}
